class CardAdditionalInfo extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.card-additional-info__trigger');
    this.tooltip = this.querySelector('.card-additional-info__tooltip');
    this.closeButton = this.querySelector('.card-additional-info__close');
    this.backdrop = null;
    this.hideTimeout = null;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');

    if (!this.trigger || !this.tooltip) return;

    this.setTooltipOpen = (isOpen) => {
      this.classList.toggle('is-open', isOpen);
      this.tooltip.classList.toggle('is-open', isOpen);
      this.tooltip.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    };

    this.unbindMobileOutsideClose = () => {
      document.removeEventListener('pointerdown', this.onMobileOutsidePointer, true);
    };

    this.onMobileOutsidePointer = (event) => {
      if (!this.mobileQuery.matches || !this.classList.contains('is-open')) return;
      if (this.tooltip.contains(event.target)) return;

      this.handleOverlayClose(event);
    };

    this.bindMobileOutsideClose = () => {
      this.unbindMobileOutsideClose();
      document.addEventListener('pointerdown', this.onMobileOutsidePointer, true);
    };

    this.handleOverlayClose = (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.closeImmediate();
    };

    this.closeImmediate = () => {
      window.clearTimeout(this.hideTimeout);
      this.unbindMobileOutsideClose();
      this.setTooltipOpen(false);
      this.tooltip.classList.remove('is-ported');

      if (this.backdrop) {
        this.backdrop.remove();
        this.backdrop = null;
      }

      if (this.tooltip.parentElement === document.body) {
        this.appendChild(this.tooltip);
      }

      document.body.classList.remove('card-additional-info--lock');
    };

    this.openMobileOverlay = () => {
      this.closeImmediate();

      this.backdrop = document.createElement('div');
      this.backdrop.className = 'card-additional-info__backdrop';
      this.backdrop.addEventListener('pointerdown', this.handleOverlayClose);

      document.body.appendChild(this.backdrop);
      document.body.appendChild(this.tooltip);
      this.tooltip.classList.add('is-ported');
      document.body.classList.add('card-additional-info--lock');
      this.setTooltipOpen(true);
      this.bindMobileOutsideClose();
    };

    this.onOpen = () => {
      if (this.mobileQuery.matches) {
        this.openMobileOverlay();
        return;
      }

      window.clearTimeout(this.hideTimeout);
      this.setTooltipOpen(true);
      requestAnimationFrame(() => this.positionTooltip());
    };

    this.onClose = () => {
      if (this.mobileQuery.matches) return;

      window.clearTimeout(this.hideTimeout);
      this.hideTimeout = window.setTimeout(() => {
        this.closeImmediate();
      }, 150);
    };

    this.onTriggerClick = (event) => {
      if (!this.mobileQuery.matches) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.classList.contains('is-open')) {
        this.handleOverlayClose(event);
      } else {
        this.openMobileOverlay();
      }
    };

    this.onMobileChange = () => {
      this.classList.toggle('is-mobile', this.mobileQuery.matches);

      if (!this.mobileQuery.matches) {
        this.closeImmediate();
      } else if (this.classList.contains('is-open')) {
        this.openMobileOverlay();
      }
    };

    this.addEventListener('mouseenter', () => {
      if (this.mobileQuery.matches) return;
      this.onOpen();
    });
    this.addEventListener('mouseleave', () => {
      if (this.mobileQuery.matches) return;
      this.onClose();
    });
    this.addEventListener('focusin', (event) => {
      if (this.mobileQuery.matches) return;
      if (this.contains(event.target)) this.onOpen();
    });
    this.addEventListener('focusout', (event) => {
      if (this.mobileQuery.matches) return;
      if (!this.contains(event.relatedTarget)) this.onClose();
    });

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.closeButton?.addEventListener('pointerdown', this.handleOverlayClose);

    this.mobileQuery.addEventListener('change', this.onMobileChange);
    this.onMobileChange();

    window.addEventListener('resize', () => {
      if (this.classList.contains('is-open') && !this.mobileQuery.matches) {
        this.positionTooltip();
      }
    });
    window.addEventListener(
      'scroll',
      () => {
        if (this.classList.contains('is-open') && !this.mobileQuery.matches) {
          this.positionTooltip();
        }
      },
      { passive: true }
    );
  }

  disconnectedCallback() {
    window.clearTimeout(this.hideTimeout);
    this.mobileQuery?.removeEventListener('change', this.onMobileChange);
    this.unbindMobileOutsideClose?.();
    this.closeImmediate();
  }

  measureHorizontalOverflow(alignRight) {
    this.tooltip.classList.toggle('card-additional-info__tooltip--align-right', alignRight);
    const rect = this.tooltip.getBoundingClientRect();
    const padding = 12;
    const leftOverflow = Math.max(0, padding - rect.left);
    const rightOverflow = Math.max(0, rect.right - (window.innerWidth - padding));

    return leftOverflow + rightOverflow;
  }

  positionTooltip() {
    const { tooltip } = this;
    const padding = 12;

    tooltip.classList.remove(
      'card-additional-info__tooltip--align-right',
      'card-additional-info__tooltip--below'
    );

    if (this.mobileQuery.matches) return;

    const leftOverflow = this.measureHorizontalOverflow(false);
    const rightOverflow = this.measureHorizontalOverflow(true);

    tooltip.classList.toggle(
      'card-additional-info__tooltip--align-right',
      rightOverflow < leftOverflow
    );

    let rect = tooltip.getBoundingClientRect();
    if (rect.top < padding) {
      tooltip.classList.add('card-additional-info__tooltip--below');
      rect = tooltip.getBoundingClientRect();
    }

    if (rect.bottom > window.innerHeight - padding) {
      tooltip.classList.remove('card-additional-info__tooltip--below');
    }
  }
}

customElements.define('card-additional-info', CardAdditionalInfo);
